import { Datadog } from '@cash-rewards/datadog';
import { MparticleProvider } from '@cash-rewards/reactjs';
import App, { AppContext, AppProps } from 'next/app';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect } from 'react';

import '@/styles/globals.css';

import { pageView } from '@/lib/gtm';

import { MParticleConfig } from '@/customTypes/tracking/MparticleConfig';

const MyApp = ({
  globalHeaderUrl,
  globalFooterUrl,
  CR_ENV_VARS_STRINGIFIED,
  Component,
  pageProps,

  gaContainerId,
  gtm3ContainerId,
  gtm3Auth,
  gtmPreview,
  mParticleConfig,
  environmentName,
  version
}: AppProps & {
  globalHeaderUrl: string;
  globalFooterUrl: string;
  CR_ENV_VARS_STRINGIFIED: string;
  gaContainerId: string;
  gtm3ContainerId: string;
  gtm3Auth: string;
  gtmPreview: string;
  mParticleConfig: MParticleConfig;
  environmentName: string;
  version: string;
}) => {
  const router = useRouter();
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      pageView(url);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('hashChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('hashChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      <Script
        id='cr-global-header'
        type='module'
        src={globalHeaderUrl}
      ></Script>
      <Script
        id='cr-global-footer'
        type='module'
        src={globalFooterUrl}
      ></Script>

      <Script
        id='crExposeRuntimeEnvVars'
        dangerouslySetInnerHTML={{
          __html: `window.CR_ENV_VARS = ${CR_ENV_VARS_STRINGIFIED}`
        }}
      />

      <Script src='https://cdn.optimizely.com/js/22882550306.js'></Script>

      <Script
        id='gtm3tag-init'
        strategy='afterInteractive'
      >{`(function (w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js',
        });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src =
          'https://www.googletagmanager.com/gtm.js?id=' +
          i +
          dl +
          '&gtm_auth=${gtm3Auth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x';
        f.parentNode.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', '${gtm3ContainerId}');`}</Script>

      <Script id='gtag-init' strategy='afterInteractive'>
        {`(function (w, d, s, l, i) {
      w[l] = w[l] || []; w[l].push({
        'gtm.start':
          new Date().getTime(), event: 'gtm.js'
      }); var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : ''; j.async = true; j.src =
          'https://www.googletagmanager.com/gtm.js?id=' + i + dl; f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', '${gaContainerId}');
            `}
      </Script>

      <MparticleProvider
        apiKey={mParticleConfig.apiKey}
        appName={mParticleConfig.applicationName}
        env={environmentName}
        dataPlan={mParticleConfig.dataPlan}
        dataPlanVersion={mParticleConfig.dataPlanVersion}
      >
        <Datadog
          config={{
            applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ?? '',
            clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
            environment: environmentName,
            version: version,
            serviceName: 'shop-ui',
          }}
        >
          <Component {...pageProps} />
        </Datadog>
      </MparticleProvider>
    </>
  );
};

MyApp.getInitialProps = async (context: AppContext) => {
  const appProps = await App.getInitialProps(context);

  const filteredEntries = Object.keys(process.env)
    .filter((key) => key.startsWith('NEXT_PUBLIC_'))
    .map((key) => [key, process.env[key]]);
  const exposedVars = Object.fromEntries(filteredEntries);

  const env = process.env.NEXT_PUBLIC_ENVIRONMENT ?? 'development';

  const result = {
    ...appProps,
    CR_ENV_VARS_STRINGIFIED: JSON.stringify(exposedVars),
    globalHeaderUrl: process.env.NEXT_PUBLIC_GLOBAL_HEADER_URL,
    globalFooterUrl: process.env.NEXT_PUBLIC_GLOBAL_FOOTER_URL,
    gaContainerId: process.env.NEXT_PUBLIC_GA4_CONTAINER_ID,
    gtm3ContainerId: process.env.NEXT_PUBLIC_GTM3_CONTAINER_ID,
    environmentName:
      process.env.APP_ENV === 'production'
        ? 'Live'
        : process.env.NEXT_PUBLIC_ENVIRONMENT,
    gtm3Auth: process.env.NEXT_PUBLIC_GTM3_AUTH,
    gtmPreview: process.env.NEXT_PUBLIC_GTM3_PREVIEW,
    mParticleConfig: {
      isDevMode: env !== 'prelive' && env !== 'live',
      trackingServer: process.env.NEXT_PUBLIC_MPARTICLE_TRACKING_SERVER,
      dataPlan: process.env.NEXT_PUBLIC_MPARTICLE_DATA_PLAN,
      dataPlanVersion: process.env.NEXT_PUBLIC_MPARTICLE_DATA_PLAN_VERSION,
      apiKey: process.env.NEXT_PUBLIC_MPARTICLE_API_KEY,
      applicationName: 'shop-ui'
    },
    version: process.env.NEXT_PUBLIC_VERSION
  };

  return result;
};

export default MyApp;
